import { ctcKuwaitDayRange } from "./ctcReports";

/*
 * Typesense filters and sorts for the CTC lists (Watch list, Purchase orders, Transactions). The documents
 * are built in functions-ctc/src/search.ts. Customer scoping is NOT done here: bank users' search key
 * (customClaims → ctcSearchKey) already carries customerId, and Typesense adds it to every search.
 */

/**
 * "Bell" documents (ctc_search_updates/<id>), rung by the Typesense triggers once Typesense has a change, so open
 * lists search again right away (Typesense can't push). Desk lists listen to "<kind>"; a bank user only to
 * "<kind>_<their customerId>" (the rules allow nothing else), so they never hear about other customers.
 */
export const CTC_SEARCH_UPDATES = "ctc_search_updates";
export type CtcSearchKind = "orders" | "settlements";
export const ctcSearchUpdateDocIds = (kind: CtcSearchKind, customerId?: string | null) =>
    [kind, ...(customerId ? [`${kind}_${customerId}`] : [])];

/** A value inside a Typesense filter, quoted so commas, spaces or backticks can't change the filter. */
export const ctcSearchQuote = (value: string) => `\`${value.replace(/`/g, "\\`")}\``;

const list = (values: string[]) => `[${values.map(ctcSearchQuote).join(",")}]`;

/** "YYYY-MM-DD" Kuwait days → created between the start of `from` and the end of `to` (unix seconds). */
const createdBetween = (from?: string | null, to?: string | null) => {
    const parts: string[] = [];
    if (from) parts.push(`created:>=${Math.floor(ctcKuwaitDayRange(from, from).start.getTime() / 1000)}`);
    if (to) parts.push(`created:<${Math.floor(ctcKuwaitDayRange(to, to).end.getTime() / 1000)}`);
    return parts;
};

export interface CtcOrderSearchFilters {
    status?: string | null; // tags.poStatus.code
    lanes?: string[]; // Watch list: orders waiting on these lanes
    dealerEmail?: string | null; // Watch list "Only orders assigned to me"
    from?: string | null; // created on or after this Kuwait day
    to?: string | null; // created on or before this Kuwait day
}

export const ctcOrderSearchFilter = ({ status, lanes, dealerEmail, from, to }: CtcOrderSearchFilters): string => [
    status ? `status:=${ctcSearchQuote(status)}` : "",
    lanes ? `lane:=${list(lanes.length ? lanes : ["none"])}` : "",
    dealerEmail ? `assignedDealerEmail:=${ctcSearchQuote(dealerEmail)}` : "",
    ...createdBetween(from, to),
].filter(Boolean).join(" && ");

export type CtcSettlementView = "open" | "customer_unsettled" | "broker_unsettled" | "settled" | "all";

/** Transactions views; settlements of orders cancelled after completion (deleted) never show. */
export const ctcSettlementSearchFilter = (view: CtcSettlementView, range: { from?: string | null; to?: string | null } = {}): string => [
    "deleted:=false",
    view === "open" ? "settled:=false" :
        view === "settled" ? "settled:=true" :
            view === "customer_unsettled" ? "customerStatus:=unsettled" :
                view === "broker_unsettled" ? "brokerStatus:=unsettled" : "",
    ...createdBetween(range.from, range.to),
].filter(Boolean).join(" && ");

/** List sort → Typesense field. */
export const CTC_ORDER_SORT_FIELDS: Record<"reference" | "beneficiary" | "amount" | "created" | "updated", string> = {
    reference: "referenceNumber",
    beneficiary: "beneficiaryNumber",
    amount: "amount",
    created: "created",
    updated: "updated",
};

/** InstantSearch index name that sorts (typesense-instantsearch-adapter: "<collection>/sort/<field>:<dir>"). */
export const ctcSortedIndex = (collection: string, field: string, dir: "asc" | "desc") => `${collection}/sort/${field}:${dir}`;
